/**
=========================================================

* Tazi Rides Ltd has developed this dashboard based on a further configured version of the original template.
* Website: https://www.tazirides.co.uk
* All rights reserved.

=========================================================
* Material Dashboard 2 PRO React TS - v1.0.2
=========================================================

* Product Page: https://www.creative-tim.com/product/material-dashboard-2-pro-react-ts
* Copyright 2023 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.

* This template has been built upon, with additional code and configuration by Tazi Rides Ltd.
*/

// @mui material components

import { useEffect } from 'react'

import Card from '@mui/material/Card'
import { useQuery } from '@tanstack/react-query'
import axios from 'axios'
import { useLocation, useNavigate } from 'react-router-dom'

import MDBox from 'components/MDBox'
import MDButton from 'components/MDButton'
import MDTypography from 'components/MDTypography'
import { useAuth } from 'context/auth'
import { updateRoutes, useMaterialUIController } from 'context/materialUI'
import FeedbackPage from 'layouts/feedback/internal'
import DashboardLayout from 'modules/LayoutContainers/DashboardLayout'
import LoadingComponent from 'modules/Loading'
import DashboardNavbar from 'modules/Navbars/DashboardNavbar'
import DataTable from 'modules/Tables/DataTable'
import { ROUTE_PATHS } from 'routes'
import { TabularData } from 'types/server/tabular'

import { FEEDBACK_TABLE_DEFAULT_FILTERS } from './constants'

function FeedbackTable(): JSX.Element {
	const [controller, dispatch] = useMaterialUIController()
	const { user } = useAuth()
	const { routes } = controller
	const navigate = useNavigate()
	const location = useLocation()
	const searchParams = new URLSearchParams(location.search)
	const urlFilters = FEEDBACK_TABLE_DEFAULT_FILTERS.reduce<Record<string, string>>((acc, key) => {
		const value = searchParams.get(key)
		if (value) acc[key] = value
		return acc
	}, {})

	const fetchData = async () => {
		const response = await axios.get<TabularData>(
			`${process.env.REACT_APP_API_URL}/feedback/table`,
			{
				headers: {
					Authorization: await user.auth.getIdToken(),
				},
			}
		)
		return response.data
	}

	const { data, error, isLoading } = useQuery({
		queryKey: ['feedback-table'],
		queryFn: fetchData,
	})

	useEffect(() => {
		if (data) {
			const feedbackRoutes = data.table.rows.map(feedback => ({
				type: 'invisible' as const,
				name: `Feedback: ${feedback.id}`,
				key: `feedback-${feedback.id}`,
				route: `${ROUTE_PATHS.FEEDBACK.BASE}/${feedback.id}`,
				component: <FeedbackPage />,
				authRequired: true,
			}))

			updateRoutes(dispatch, routes, feedbackRoutes)
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [data, dispatch])

	return (
		<DashboardLayout>
			<DashboardNavbar />
			<MDBox pt={6} pb={3}>
				<Card>
					<MDBox
						display={'flex'}
						flexDirection={'row'}
						alignItems={'center'}
						justifyContent={'space-between'}
						p={3}
					>
						<MDBox lineHeight={1}>
							<MDTypography variant="h2" fontWeight="medium">
								Feedback Overview
							</MDTypography>
							<MDTypography variant="button" color="text">
								View and manage all feedback that you have received.
							</MDTypography>
						</MDBox>
						<MDButton
							variant="outlined"
							color="primary"
							onClick={() => navigate(ROUTE_PATHS.FEEDBACK.NEW)}
						>
							+ new feedback
						</MDButton>
					</MDBox>
					{!isLoading && !error ? (
						<DataTable
							table={data.table}
							parentPath={ROUTE_PATHS.FEEDBACK.BASE}
							canSearch
							filters={data.filters}
							initialFilter={Object.keys(urlFilters).length > 0 ? urlFilters : undefined}
						/>
					) : (
						<LoadingComponent type="box" height={600} containerStyle={{ padding: 50 }} />
					)}
				</Card>
			</MDBox>
		</DashboardLayout>
	)
}

export default FeedbackTable
